import type { JsonObject } from '@seen/core/json'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'

export type DeskView = 'canvas' | 'list'

/* Which of a desk's two renderings is showing. Keyed by the union so a third
 * view can't be added without every consumer being forced to handle it. */
const DESK_VIEWS: Record<DeskView, true> = { canvas: true, list: true }

export function isDeskView(value: unknown): value is DeskView {
  // `hasOwn`, not `in` — `in` walks the prototype chain, so a stored value of
  // 'toString' would pass.
  return typeof value === 'string' && Object.hasOwn(DESK_VIEWS, value)
}

export const DEFAULT_DESK_VIEW: DeskView = 'canvas'

/* Browser-level (localStorage) memory of the last view each desk was left in,
 * keyed by deskId so switching one desk to list view doesn't move every other
 * desk with it. It's a per-browser UI preference, deliberately not stored on
 * the Desk (which is shared) or the Practitioner. Mirrors the persist pattern
 * in useNotesViewStore; unset desks fall back to 'canvas'. */
export const useDeskViewStore = create(
  devtools(
    persist(
      combine({ viewByDeskId: {} as Record<string, DeskView> }, (set) => ({
        setDeskView: (deskId: string, view: DeskView) => {
          set((state) => ({
            viewByDeskId: { ...state.viewByDeskId, [deskId]: view },
          }))
        },
      })),
      {
        name: 'desk-view-store',
        version: 1,
        /* Drop any desk whose stored value isn't a known view — a hand-edited
         * or stale entry would otherwise render a switcher with neither side
         * active.
         *
         * This lives in `merge`, not `migrate`: persist only calls `migrate`
         * when the stored version differs from `version`, and every write
         * stamps the current version, so a migrate-based guard is dead code on
         * all real data. `merge` runs on every rehydrate — the only path bad
         * localStorage can enter by. */
        merge: (persisted, current) => {
          const stored = (
            persisted as { viewByDeskId?: JsonObject } | undefined
          )?.viewByDeskId
          const viewByDeskId: Record<string, DeskView> = {}
          for (const [deskId, view] of Object.entries(stored ?? {})) {
            if (isDeskView(view)) {
              viewByDeskId[deskId] = view
            }
          }
          return { ...current, viewByDeskId }
        },
      }
    )
  )
)

export function useDeskView(deskId: string): DeskView {
  return useDeskViewStore(
    (state) => state.viewByDeskId[deskId] ?? DEFAULT_DESK_VIEW
  )
}
